import { useEffect, useRef, useState } from "react";
import axios from "axios";

const PATH_TO_BACKEND = process.env.REACT_APP_PATH_TO_BACKEND;
const DEBOUNCE_MS = 500;

// Умный поиск по каталогу (KAL-59, search-service). Как на складе Мурино
// (WarehouseMurinoPrices.js): raw-текст поиска (search) сначала дебаунсится в
// debouncedSearch, и уже debouncedSearch (а не живой search) отдаётся наружу
// для фильтрации в groupCatalogItems и управляет запросом к search-service.
// Пока человек печатает, ни debouncedSearch, ни smartScoreByName не меняются —
// список не мигает локальным .includes()-фильтром на каждую нажатую клавишу.
// smartScoreByName инвалидируется РОВНО один раз — синхронно со сменой
// debouncedSearch — и подменяется на настоящую карту, когда придёт ответ.
// Это гарантирует, что карта всегда либо null, либо посчитана именно под
// ТЕКУЩИЙ debouncedSearch: она не может "пережить" смену запроса и
// отфильтровать список по чужому, более старому тексту (в отличие от версии,
// где карта держалась поверх живого search).
export const useCatalogSearch = (search) => {
    const [debouncedSearch, setDebouncedSearch] = useState(() => search.trim());
    const [smartScoreByName, setSmartScoreByName] = useState(null);
    const [isSmartSearching, setIsSmartSearching] = useState(false);
    const debounceTimeoutRef = useRef(null);
    const requestIdRef = useRef(0);

    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
        };
    }, []);

    // Дебаунс raw-текста -> debouncedSearch. Очистка поля применяется мгновенно
    // (без дебаунса), как и на складе Мурино.
    useEffect(() => {
        const query = search.trim();
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

        if (!query) {
            setDebouncedSearch("");
            return;
        }

        debounceTimeoutRef.current = setTimeout(() => setDebouncedSearch(query), DEBOUNCE_MS);
    }, [search]);

    // Умный поиск через search-service — реагирует только на устоявшийся
    // (debouncedSearch), а не на каждую нажатую клавишу.
    useEffect(() => {
        const requestId = ++requestIdRef.current;

        if (!debouncedSearch) {
            setSmartScoreByName(null);
            setIsSmartSearching(false);
            return;
        }

        setSmartScoreByName(null);
        setIsSmartSearching(true);

        axios
            .post(`${PATH_TO_BACKEND}/api/adminka/product-catalog/search-names`, { q: debouncedSearch })
            .then((response) => {
                if (requestId !== requestIdRef.current) return;
                const data = response.data;
                if (data?.success && !data.searchServiceUnavailable && Array.isArray(data.data) && data.data.length > 0) {
                    setSmartScoreByName(new Map(data.data.map(({ name, score }) => [name, score])));
                } else {
                    setSmartScoreByName(null);
                }
            })
            .catch((error) => {
                if (requestId !== requestIdRef.current) return;
                console.warn("Умный поиск по каталогу недоступен, используется локальный фильтр:", error.message);
                setSmartScoreByName(null);
            })
            .finally(() => {
                if (requestId === requestIdRef.current) setIsSmartSearching(false);
            });
    }, [debouncedSearch]);

    return { smartScoreByName, isSmartSearching, debouncedSearch };
};
